import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useVehicles } from 'hooks/useVehicles/useVehicles';
import { useTranslation } from 'react-i18next';
import { Icon, Field } from '@ontruck/orc';
import ButtonSelector from 'components/ButtonSelector';
import ButtonLoading from 'components/ButtonLoading';
import {
  VEHICLE_ICONS,
  VEHICLE_TYPES,
  VEHICLES_EXTRAS,
} from 'utils/constants/vehicles';

const FastVehicleCreation = ({
  className,
  journeyId,
  refreshJourney,
  currentAssignedVehicles,
}) => {
  const {
    handleShowFastVehicleCreation,
    candidatePlateNumber,
    setCandidatePlateNumber,
    candidateType,
    setCandidateType,
    createAndAssignVehicle,
    loading,
    error,
    candidateExtras,
    handleAddVehicleExtras,
    fetchSuitableVehicles,
    checkIfVehicleAlreadyExists,
  } = useVehicles();
  const { t } = useTranslation('PendingJobDetail');
  const [vehicleAlreadyExists, setVehicleAlreadyExists] = useState(false);

  const handleClose = useCallback(() => {
    handleShowFastVehicleCreation(false);
  }, [handleShowFastVehicleCreation]);

  const handleChangePlate = useCallback(
    (e, { value }) => {
      setCandidatePlateNumber(value);
      setVehicleAlreadyExists(checkIfVehicleAlreadyExists(value));
    },
    [setCandidatePlateNumber, checkIfVehicleAlreadyExists]
  );

  const handleVehicleType = useCallback(
    (e, { value }) => {
      setCandidateType(value);
    },
    [setCandidateType]
  );

  const handleToggleExtra = useCallback(
    (e, { checked, name, value }) => {
      handleAddVehicleExtras({ [name]: checked });
    },
    [handleAddVehicleExtras]
  );

  const handleSubmit = useCallback(() => {
    createAndAssignVehicle(journeyId, refreshJourney, currentAssignedVehicles);
    fetchSuitableVehicles(journeyId);
  }, [
    createAndAssignVehicle,
    journeyId,
    refreshJourney,
    currentAssignedVehicles,
    fetchSuitableVehicles,
  ]);

  const isDisabled =
    (candidatePlateNumber === '' && candidateType === null) ||
    vehicleAlreadyExists;

  return (
    <div className={className} data-testid="fast-vehicle-creation">
      <div className="fast-vehicle-creation">
        <button className="fast-vehicle-creation__close" onClick={handleClose}>
          <Icon glyph="close" size="2rem" />
        </button>
        <div className="fast-vehicle-creation__container">
          <div className="fast-vehicle-creation__header">
            <p className="fast-vehicle-creation__title">
              {t('registerVehicle')}
            </p>
            <p className="fast-vehicle-creation__subtitle">
              {t('registerVehicleOnlyByLoad')}
            </p>
          </div>
          <div className="fast-vehicle-creation__options">
            <p className="fast-vehicle-creation__options-title">
              {t('vehicleType')}
            </p>
            <ButtonSelector
              type="radio"
              value={candidateType}
              onChange={handleVehicleType}
              disabled={loading}
            >
              <ButtonSelector.Item
                icon={VEHICLE_ICONS.tractor_unit}
                text={t('tractor_unit')}
                itemValue={VEHICLE_TYPES.tractor_unit}
                name="vehicle_type"
                testId="fast-vehicle-creation__options-tractor"
              />
              <ButtonSelector.Item
                icon={VEHICLE_ICONS.trailer}
                text={t('trailer')}
                itemValue={VEHICLE_TYPES.trailer}
                name="vehicle_type"
                testId="fast-vehicle-creation__options-trailer"
              />
            </ButtonSelector>
          </div>
          {candidateType === VEHICLE_TYPES.trailer && (
            <div className="fast-vehicle-creation__extras">
              <p className="fast-vehicle-creation__extras-title">
                {t('extrasTitle')}
              </p>
              <div className="fast-vehicle-creation__extra-item">
                <ButtonSelector
                  type="checkbox"
                  value={
                    candidateExtras[VEHICLES_EXTRAS.has_lateral_loading] ||
                    false
                  }
                  onChange={handleToggleExtra}
                  disabled={loading}
                >
                  <ButtonSelector.Item
                    icon={VEHICLE_ICONS.sideload}
                    text={t('sideLoad')}
                    name={VEHICLES_EXTRAS.has_lateral_loading}
                    testId="fast-vehicle-creation__extra-sideload"
                  />
                </ButtonSelector>
              </div>
            </div>
          )}
          {candidateType !== null && (
            <>
              <div className="fast-vehicle-creation__plate">
                <label className="fast-vehicle-creation__plate-label">
                  {t('plate')}
                </label>
                <Field
                  id="vehicle_plate"
                  name="vehicle_plate"
                  className={'fast-vehicle-creation__plate-input'}
                  size="L"
                  label={t('plate')}
                  value={candidatePlateNumber}
                  onChange={handleChangePlate}
                  labelType="hidden"
                  disabled={loading}
                />
                {error && (
                  <span className="fast-vehicle-creation__plate-error">
                    {t('errorVehicleInUse')}
                  </span>
                )}
                {vehicleAlreadyExists && (
                  <span className="fast-vehicle-creation__plate-error">
                    {t('errorVehicleAlreadyRegister')}
                  </span>
                )}
              </div>
              <div className="fast-vehicle-creation__action">
                <ButtonLoading
                  fullWidth
                  className="fast-vehicle-creation__action-button"
                  testId="fast-vehicle-creation__submit"
                  loading={loading}
                  disabled={isDisabled}
                  handleClick={handleSubmit}
                  text={t('registerAndAssign')}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

FastVehicleCreation.defaultProps = {
  currentAssignedVehicles: [],
};

FastVehicleCreation.propTypes = {
  className: PropTypes.string,
  journeyId: PropTypes.number.isRequired,
  refreshJourney: PropTypes.func.isRequired,
  currentAssignedVehicles: PropTypes.array,
};
export default FastVehicleCreation;
